import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const QuestionnairesList = ({ initialQuestionnaires = [] }) => {
  const [questionnaires, setQuestionnaires] = useState(initialQuestionnaires);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (initialQuestionnaires.length === 0) {
      fetchQuestionnaires();
    }
  }, []);

  const fetchQuestionnaires = async () => {
    setLoading(true);
    try {
      const response = await fetch('/questionnaires.json');
      if (response.ok) {
        const data = await response.json();
        setQuestionnaires(data);
      }
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (questionnaire) => {
    if (!confirm(`Create a copy of "${questionnaire.name}"?`)) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken);
      }

      const response = await fetch(`/questionnaires/${questionnaire.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Redirect to edit the new questionnaire
          window.location.href = `/questionnaires/${data.questionnaire.id}/edit`;
        }
      } else {
        swal('Error', 'Failed to duplicate questionnaire', 'error');
      }
    } catch (error) {
      console.error('Error duplicating questionnaire:', error);
      swal('Error', 'Error duplicating questionnaire', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (questionnaire) => {
    const result = await swal({
      title: 'Delete this questionnaire?',
      text: `Are you sure you want to delete "${questionnaire.name}"? This action cannot be undone.`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });
    
    if (!result.value) return;
    
    setLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      const response = await fetch(`/questionnaires/${questionnaire.id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setQuestionnaires(prev => prev.filter(q => q.id !== questionnaire.id));
        } else {
          swal('Error', data.error || 'Failed to delete questionnaire', 'error');
        }
      } else {
        swal('Error', 'Failed to delete questionnaire', 'error');
      }
    } catch (error) {
      console.error('Error deleting questionnaire:', error);
      swal('Error', 'Error deleting questionnaire', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedQuestionnaires = questionnaires
    .filter(q => 
      q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (q.description && q.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'created_at' || sortBy === 'updated_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return 'fa-sort';
    return sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && questionnaires.length === 0) {
    return (
      <div className="text-center py-4">
        <i className="fa fa-spinner fa-spin"></i> Loading questionnaires...
      </div>
    );
  }

  return (
    <div className="questionnaires-list">
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text">
                <i className="fa fa-search"></i>
              </span>
            </div>
            <input
              type="text"
              className="form-control"
              placeholder="Search questionnaires..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-6 text-right">
          <a href="/questionnaires/new" className="btn btn-primary">
            <i className="fa fa-plus"></i> Add Questionnaire
          </a>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th 
                className="sortable" 
                onClick={() => handleSort('name')}
                style={{ cursor: 'pointer' }}
              >
                Name <i className={`fa ${getSortIcon('name')}`}></i>
              </th>
              <th>Description</th>
              <th 
                className="sortable text-center" 
                onClick={() => handleSort('question_count')}
                style={{ cursor: 'pointer' }}
              >
                Questions <i className={`fa ${getSortIcon('question_count')}`}></i>
              </th>
              <th className="text-center">Used In Workflows</th>
              <th 
                className="sortable text-center" 
                onClick={() => handleSort('updated_at')}
                style={{ cursor: 'pointer' }}
              >
                Last Modified <i className={`fa ${getSortIcon('updated_at')}`}></i>
              </th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedQuestionnaires.map(questionnaire => (
              <tr key={questionnaire.id}>
                <td>
                  <a href={`/questionnaires/${questionnaire.id}`} className="no-decoration">
                    <strong>{questionnaire.name}</strong>
                  </a>
                </td>
                <td>
                  <span className="text-muted">
                    {questionnaire.description || 'No description'}
                  </span>
                </td>
                <td className="text-center">
                  <span className="badge badge-secondary">
                    {questionnaire.question_count}
                  </span>
                </td>
                <td className="text-center">
                  <span className="badge badge-info">
                    {questionnaire.workflows_count}
                  </span>
                </td>
                <td className="text-center text-muted">
                  {formatDate(questionnaire.updated_at)}
                </td>
                <td className="text-center">
                  <div className="btn-group">
                    <a 
                      href={`/questionnaires/${questionnaire.id}`}
                      className="btn btn-link btn-sm"
                      title="View"
                    >
                      <i className="fa fa-eye"></i>
                    </a>
                    <a 
                      href={`/questionnaires/${questionnaire.id}/edit`}
                      className="btn btn-link btn-sm"
                      title="Edit"
                    >
                      <i className="fa fa-pencil-alt"></i>
                    </a>
                    <button
                      className="btn btn-link btn-sm"
                      onClick={() => handleDuplicate(questionnaire)}
                      disabled={loading}
                      title="Duplicate"
                    >
                      <i className="fa fa-copy"></i>
                    </button>
                    <button
                      className="btn btn-link btn-sm text-danger"
                      onClick={() => handleDelete(questionnaire)}
                      disabled={loading || questionnaire.workflows_count > 0}
                      title={questionnaire.workflows_count > 0 ? "Cannot delete - used in workflows" : "Delete"}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredAndSortedQuestionnaires.length === 0 && !loading && (
        <div className="text-center py-4">
          <p className="text-muted">
            {searchTerm ? 'No questionnaires found matching your search.' : 'No questionnaires yet.'}
          </p>
          {!searchTerm && (
            <a href="/questionnaires/new" className="btn btn-primary">
              <i className="fa fa-plus"></i> Create Your First Questionnaire
            </a>
          )}
        </div>
      )}

      {loading && (
        <div className="overlay">
          <div className="overlay-content">
            <i className="fa fa-spinner fa-spin"></i> Processing...
          </div>
        </div>
      )}

      <style>{`
        .overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .overlay-content {
          background: white;
          padding: 2rem;
          border-radius: 0.25rem;
          color: #333;
        }
        
        .sortable:hover {
          background-color: rgba(0,0,0,0.05);
        }
      `}</style>
    </div>
  );
};

QuestionnairesList.propTypes = {
  initialQuestionnaires: PropTypes.array
};

export default QuestionnairesList;